import * as m from "mithril";
import { NavBar } from '../../components/NavBar';
import AppContext from '../../context/AppContext';
import BusLayout from "./BusLayout";
import LoadingIndicator from "../../components/LoadingIndicator";
import './BusLayoutPage.scss';
import { MessageProvider } from "../../context/AppMessages";
import TripSearchResultSummaryView from "../../components/TripSearchResultSummaryView";
import buildLanguageChangeActions from "../common/LanguageNavActions";
import { getIntlStringValue } from "../../helpers/StringHelpers";
import CustomSvgIcon from "../../components/CustomSvgIcon";
import { InternationalStringDTO, MoneyDTO, SeatDTO, TripDTO, TripResultDTO } from "../../apiclient/ApiDtos";
import SearchCriteria from "../../models/SearchCriteria";
import { formatMoney } from "../../helpers/MoneyFormatter";


interface SelectedSeatComponentAttrs {
    seat: SeatDTO;
}
export function SelectedSeatComponent(): m.Component<SelectedSeatComponentAttrs> {
    return {
        view(vnode) {
            let seatFeatureClass = "";
            if (vnode.attrs.seat.priceLevel.max >= 3 &&
                vnode.attrs.seat.priceLevel.value == 1) {
                seatFeatureClass = "seat-price-lowest";
            } else if (vnode.attrs.seat.priceLevel.max >= 3 &&
                vnode.attrs.seat.priceLevel.value == vnode.attrs.seat.priceLevel.max) {
                seatFeatureClass = "seat-price-highest";
            } else if (vnode.attrs.seat.priceLevel.max >= 2 &&
                vnode.attrs.seat.priceLevel.value == vnode.attrs.seat.priceLevel.max) {
                seatFeatureClass = "seat-price-highest";
            } else {
                seatFeatureClass = "seat-price-normal";
            }

            return m("span", {
                class: "seat-selection-slot en-only mr-2 " + seatFeatureClass
            },
                vnode.attrs.seat.number);
        }
    };
}

interface Attrs {
    appContext: AppContext,
    hidePricePopAnimation: boolean,
    searchCriteria: SearchCriteria,
    seatSelections: SeatDTO[],
    isSubmittingSeatReservation: boolean,
    tripResult: TripResultDTO,
    tripDetails: TripDTO | null,
    messageProvider: MessageProvider,
    errorMessage: InternationalStringDTO | null,
    onBackPressed: () => void,
    onSelectSeat: (seat: SeatDTO) => void,
    onUnselectSeat: (seat: SeatDTO) => void,
    onSeatReservationRequested: (seats: SeatDTO[]) => void
}


function BusLayoutPage(): m.Component<Attrs> {
    let isHidingSeatSelectionDetails: boolean = true;
    let isListeningScrollOnMain = false;

    let isScrolledToErrorMessageView = false;

    return {
        view: function (vnode) {
            const { appContext, seatSelections, tripResult, tripDetails, onBackPressed } = vnode.attrs;
            let currLang = vnode.attrs.messageProvider.getCurrentLang();
            let notices: InternationalStringDTO[] = vnode.attrs.tripResult?.notices || [];

            let langChangeActionItems = buildLanguageChangeActions(vnode.attrs.appContext);
            let totalSeatPriceCurrency = "MMK";
            let totalSelectedSeatsPriceAmount = seatSelections.reduce((total, seat) => (total + seat.price.amount), 0);
            let totalSeatPrice: MoneyDTO = {
                amount: totalSelectedSeatsPriceAmount,
                currency: totalSeatPriceCurrency
            }

            let isMultipleSeatPricesTrip = tripDetails != null && tripDetails.priceRange.min.amount < tripDetails.priceRange.max.amount;

            if (vnode.attrs.errorMessage != null && !isScrolledToErrorMessageView && !vnode.attrs.isSubmittingSeatReservation) {
                setTimeout(() => {
                    document
                        .getElementById("seat-reservation-error-msg")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 0);
                isScrolledToErrorMessageView = true;
            }
            return m("div.app-layout", [
                m(NavBar, {
                    title: vnode.attrs.appContext.getMessageProvider().get('nav_title.select_seat'),
                    canBackPress: true,
                    actionItems: langChangeActionItems,
                    onBackPressed: () => {
                        vnode.attrs.onBackPressed();
                    }
                }),

                m("div.app-area-below-nav",
                ),

                m("div.app-area-main",
                    /* isListeningScrollOnMain ? {
                        onscroll: (e: UIEvent) => {
                            isHidingSeatSelectionDetails = true;
                            isListeningScrollOnMain = false;
                        }
                    } :
                        {}
                    , */
                    [
                        m("div.card shadow-sm",
                            m("div.card-body p-3", [

                                m(TripSearchResultSummaryView, {
                                    tripResult: vnode.attrs.tripResult,
                                    numberOfSeats: vnode.attrs.searchCriteria.numberOfSeats,
                                    messageProvider: vnode.attrs.messageProvider,
                                    showingDate: true,
                                    showAsMultipleSeatPrice: true
                                }),

                                (notices.length > 0) ?
                                    m("div", { class: "alert alert-info mt-2 mb-0 p-2" },
                                        [
                                            m("small.font-weight-bold",
                                                vnode.attrs.appContext.getMessageProvider().get("trip.operator_notices", {
                                                    name: getIntlStringValue(tripResult.operator.name, currLang)
                                                })
                                            ),
                                            notices.map(notice => {
                                                return m("div", { class: "small" }, "- " + getIntlStringValue(notice, currLang))
                                            })
                                        ]
                                    )
                                    : null,
                            ])
                        ),
                        vnode.attrs.errorMessage != null ?
                            m("div", { class: "d-flex alert alert-warning mx-3 mt-2 mb-0 p-2", id: "seat-reservation-error-msg" },
                                m(CustomSvgIcon, {
                                    iconType: 'exclamation_triangle',
                                    themeCssClass: 'svg-medium svg-warning-dark'
                                }),
                                m("small.ms-1 ml-1 font-weight-bold", currLang == "en" ? vnode.attrs.errorMessage.en : vnode.attrs.errorMessage.my)
                            ) : null,
                        (tripDetails != null && tripDetails.seatPlan) ?
                            m("div.p-3",
                                m(BusLayout, {
                                    hidePricePopAnimation: vnode.attrs.hidePricePopAnimation,
                                    isDirectBookableTrip: (tripDetails == null || tripDetails.isDirectBookable),
                                    appContext: vnode.attrs.appContext,
                                    isMultipleSeatPriceTrip: isMultipleSeatPricesTrip,
                                    seatPlan: tripDetails!.seatPlan!,
                                    seatSelections: vnode.attrs.seatSelections,
                                    isSubmittingSeatReservation: vnode.attrs.isSubmittingSeatReservation,
                                    requiredSeatCount: vnode.attrs.searchCriteria.numberOfSeats,
                                    onSeatReservationRequested: function (seats: SeatDTO[]) {
                                        console.log("bus page seats", seats);
                                        vnode.attrs.onSeatReservationRequested(seats);
                                    },
                                    onSelectSeat: function (selectedOne: SeatDTO) {
                                        vnode.attrs.onSelectSeat(selectedOne);
                                        if (seatSelections.length == vnode.attrs.searchCriteria.numberOfSeats) {
                                            isHidingSeatSelectionDetails = false;
                                        }
                                    },
                                    onUnselectSeat: function (unselectOne: SeatDTO) {
                                        vnode.attrs.onUnselectSeat(unselectOne);
                                        isHidingSeatSelectionDetails = true
                                    }
                                })
                            ) :
                            m(LoadingIndicator)
                    ]
                ),
                m("div.app-area-footer", [
                    m("div", { class: "card card-elevated-1" },


                        m("div.card-body px-3 py-2", [
                            vnode.attrs.tripResult.isSeatSelectionAllowed && m("div", {
                                onclick: (event) => {
                                    isHidingSeatSelectionDetails = !isHidingSeatSelectionDetails;
                                }
                            },
                                m("div.d-flex justify-content-between align-items-center",
                                    m("div.small font-weight-bold fw-bold text-muted my-2 text-label-primary-light",
                                        vnode.attrs.searchCriteria.numberOfSeats > 1 ?
                                            appContext.getMessageProvider().get("seat_selection.select_n_seats", {
                                                numOfSeats: vnode.attrs.searchCriteria.numberOfSeats
                                            }) :

                                            appContext.getMessageProvider().get("seat_selection.select_one_seat")
                                    ),
                                    vnode.attrs.searchCriteria.numberOfSeats > 1 && seatSelections.length > 0 ?
                                        m("div.d-flex",
                                            m("div.small mr-1 text-info", vnode.attrs.messageProvider.get("price", currLang)),
                                            m(CustomSvgIcon, {
                                                iconType: isHidingSeatSelectionDetails ? 'keyboard_arrow_up' : 'keyboard_arrow_down',
                                                themeCssClass: "svg-info svg-medium"
                                            })

                                        ) : null
                                ),
                                vnode.attrs.searchCriteria.numberOfSeats > 1 && isHidingSeatSelectionDetails ?
                                    m("div.d-flex mb-2", {},
                                        seatSelections.map(function (seat) {
                                            console.debug("SeatNumber: ", seat.number)
                                            return m(SelectedSeatComponent, { seat: seat })
                                        }),
                                        vnode.attrs.searchCriteria.numberOfSeats - seatSelections.length > 0 ?
                                            Array.from({ length: vnode.attrs.searchCriteria.numberOfSeats - vnode.attrs.seatSelections.length }, (_, index) =>
                                                m(
                                                    "span",
                                                    {
                                                        key: index,
                                                        class: "seat-selection-slot mr-2"
                                                    }
                                                )
                                            ) : null,
                                    )
                                    :
                                    [
                                        seatSelections.map(function (seat) {
                                            return m("div.row my-2",
                                                m("div.col-5",
                                                    m(SelectedSeatComponent, {
                                                        seat: seat
                                                    })
                                                ),
                                                m("div.col-7.text-right",
                                                    m("span", { class: "text-dark" }, (formatMoney(seat.price)))
                                                ),
                                            )
                                        }),
                                        vnode.attrs.searchCriteria.numberOfSeats - seatSelections.length > 0 ?
                                            Array.from({ length: vnode.attrs.searchCriteria.numberOfSeats - seatSelections.length }, (_, index) =>
                                                m("div.row my-2",
                                                    m("div.col-5",
                                                        m(
                                                            "span",
                                                            {
                                                                key: index,
                                                                class: "seat-selection-slot en-only mr-2",
                                                            }
                                                        )
                                                    ),
                                                    m("div.col-7")
                                                )
                                            )
                                            : null
                                    ],

                                //Total Seat Price
                                vnode.attrs.searchCriteria.numberOfSeats > 1 && seatSelections.length > 0 ?
                                    m("div.row mt-3 mb-2",
                                        m("div.col-5",
                                            m("span", { class: "small mr-2" }, appContext.getMessageProvider().get("total_seat_price"))
                                        ),
                                        m("div.col-7.text-right",
                                            m("span", { class: "text-success font-weight-bold fw-bold" },
                                                seatSelections.length > 0 ?
                                                    formatMoney(totalSeatPrice)
                                                    : null
                                            )
                                        ),
                                    ) : null,


                            ),
                            m("div", { class: "text-right" },
                                vnode.attrs.isSubmittingSeatReservation ?
                                    m("button", {
                                        class: "btn disabled btn-primary text-nowrap form-control"
                                    },
                                        appContext.getMessageProvider().get("seat_selection.submitting")
                                    ) :
                                    m("button", {
                                        class: "btn btn-primary text-nowrap form-control",
                                        disabled: (seatSelections.length == vnode.attrs.searchCriteria.numberOfSeats) ? false : true,
                                        onclick: (event) => {
                                            event.preventDefault();
                                            if (seatSelections.length == vnode.attrs.searchCriteria.numberOfSeats) {
                                                let selectedSeats: SeatDTO[] = seatSelections;
                                                vnode.attrs.onSeatReservationRequested(selectedSeats);
                                            }

                                            isScrolledToErrorMessageView = false;
                                        }
                                    },
                                        appContext.getMessageProvider().get("seat_selection.submit")
                                    )
                            )
                            ,
                        ]
                        ),
                        appContext.isLeavingExtraMarginBottom() ?
                            m("div.app-area-system-nav") : null
                    )

                ])
            ])
        }
    }
}


export default BusLayoutPage;
